import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, useMap } from "react-leaflet";
import { Crosshair, Loader2, MapPin, Search } from "lucide-react";
import { toast } from "sonner";
import AddressAutocomplete from "./AddressAutocomplete";
import { Button } from "./ui/button";
import { geocodeAddress, reverseGeocode } from "../lib/api";
import { DEFAULT_CENTER } from "../lib/constants";

function Recenter({ lat, lng }) {
  const map = useMap();
  useEffect(() => {
    if (lat != null && lng != null) map.setView([lat, lng], 16);
  }, [lat, lng, map]);
  return null;
}

/**
 * Address + map pin picker. Three ways to land on the same coords:
 *   1. type/pick an address  → geocoded
 *   2. "I'm here"            → browser GPS, reverse-geocoded to an address
 *   3. drag the pin          → reverse-geocoded to an address
 *
 * Value shape: { address, lat, lng }. Emits the whole object on change.
 */
export default function LocationPicker({
  value,
  onChange,
  placeholder = "Street number, street, suburb",
  testIdPrefix = "loc",
}) {
  const { address = "", lat = null, lng = null } = value || {};
  const [busy, setBusy] = useState(false);
  const center = lat != null && lng != null ? [lat, lng] : DEFAULT_CENTER;

  const set = (patch) => onChange({ ...(value || {}), ...patch });

  const lookup = async () => {
    if (!address || address.length < 3) {
      toast.error("Type an address first");
      return;
    }
    setBusy(true);
    try {
      const r = await geocodeAddress(address);
      set({ lat: r.lat, lng: r.lng });
      toast.success("Found it — drag the pin if it's not exact");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't find that address");
    } finally {
      setBusy(false);
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      toast.error("This device can't share its location");
      return;
    }
    setBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        try {
          const r = await reverseGeocode(latitude, longitude);
          set({ lat: latitude, lng: longitude, address: r.address || address });
          toast.success("Pinned your current position");
        } catch {
          set({ lat: latitude, lng: longitude });
        } finally {
          setBusy(false);
        }
      },
      () => {
        setBusy(false);
        toast.error("Location permission denied — type the address instead");
      },
      { enableHighAccuracy: true, timeout: 10000 },
    );
  };

  const onPinDrag = async (e) => {
    const { lat: nlat, lng: nlng } = e.target.getLatLng();
    set({ lat: nlat, lng: nlng });
    try {
      const r = await reverseGeocode(nlat, nlng);
      if (r.address) set({ lat: nlat, lng: nlng, address: r.address });
    } catch {
      /* keep the typed address */
    }
  };

  return (
    <div className="space-y-2" data-testid={`${testIdPrefix}-picker`}>
      <AddressAutocomplete
        value={address}
        onChange={(v) => set({ address: v })}
        onPick={(p) => set({ address: p.label || address, lat: p.lat, lng: p.lng })}
        placeholder={placeholder}
        testIdPrefix={testIdPrefix}
      />
      <div className="flex flex-wrap gap-2">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={lookup}
          disabled={busy}
          data-testid={`${testIdPrefix}-find-btn`}
        >
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Search className="h-3.5 w-3.5" />}
          Find on map
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          onClick={useMyLocation}
          disabled={busy}
          data-testid={`${testIdPrefix}-gps-btn`}
        >
          <Crosshair className="h-3.5 w-3.5" />
          I'm standing here
        </Button>
      </div>

      <div className="rounded-md overflow-hidden border border-slate-700" style={{ height: 240 }}>
        <MapContainer center={center} zoom={lat != null ? 16 : 6} style={{ height: "100%", width: "100%" }}>
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap"
          />
          <Recenter lat={lat} lng={lng} />
          {lat != null && lng != null && (
            <Marker position={[lat, lng]} draggable eventHandlers={{ dragend: onPinDrag }} />
          )}
        </MapContainer>
      </div>

      <p className="text-[11px] text-slate-500 flex items-start gap-1.5">
        <MapPin className="h-3 w-3 mt-0.5 shrink-0" />
        {lat != null && lng != null ? (
          <span data-testid={`${testIdPrefix}-coords`}>
            Pinned at {lat.toFixed(5)}, {lng.toFixed(5)} — drag the pin to move the exact
            gate you tow in and out of.
          </span>
        ) : (
          <span>
            Pick an address, tap “Find on map”, or stand in the yard and tap “I'm standing
            here”. Then drag the pin onto your gate.
          </span>
        )}
      </p>
    </div>
  );
}
